import React, { FC } from 'react';
import resideoBlackLogo from 'assets/images/resideo-logo-black.png';
import { useTranslation } from 'react-i18next';
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  PDFViewer,
  Image,
  Font,
} from '@react-pdf/renderer';
import DINCondensedBold from 'assets/fonts/DIN Condensed Bold.ttf';
import RobotoItalic from 'assets/fonts/Roboto-Italic.ttf';
import RobotoRegular from 'assets/fonts/Roboto-Regular.ttf';
import RobotoBold from 'assets/fonts/Roboto-Bold.ttf';
import RobotoBoldItalic from 'assets/fonts/Roboto-BoldItalic.ttf';

Font.register({
  family: 'DINCondensedBold',
  src: DINCondensedBold,
  fontWeight: 'heavy',
});

Font.register({
  family: 'RobotoItalic',
  src: RobotoItalic,
});
Font.register({
  family: 'RobotoRegular',
  src: RobotoRegular,
});
Font.register({
  family: 'RobotoBold',
  src: RobotoBold,
});
Font.register({
  family: 'RobotoBoldItalic',
  src: RobotoBoldItalic,
});

const styles = StyleSheet.create({
  viewer: {
    width: '99%',
    height: '99%',
  },
  page: {
    backgroundColor: '#fff',
  },
  container: {
    margin: 10,
    padding: 10,
  },
  image: {
    width: '100px',
    height: '20px',
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: 'heavy',
    fontFamily: 'DINCondensedBold',
  },
  addressWrapper: {
    flexDirection: 'column',
    textAlign: 'right',
  },
  smallBoldText: {
    fontSize: 10,
    fontFamily: 'RobotoBold',
  },
  lightText: {
    fontSize: 10,
    fontFamily: 'RobotoRegular',
  },
  billToWrapper: {
    marginTop: 25,
    flexDirection: 'column',
  },
  table: {
    marginTop: 25,
  },
  automaticallyCharged: {
    fontSize: 8,
  },
  tHead: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    backgroundColor: '#ebebeb',
    fontSize: 8,
    padding: '8 16',
    fontFamily: 'RobotoBold',
    marginTop: 5,
  },
  tBody: {
    flexDirection: 'row',
    width: '100%',
    fontSize: 8,
    padding: '8 16',
    fontFamily: 'RobotoRegular',
    borderBottom: '1pt solid #ebebeb',
  },
  tData: {
    width: '100%',
  },
  totalRow: {
    flexDirection: 'row',
  },
  totalRowLeft: {
    flexDirection: 'column',
    textAlign: 'right',
    fontFamily: 'RobotoRegular',
    fontSize: 8,
    marginRight: 100,
  },
  totalRowRight: {
    flexDirection: 'column',
    textAlign: 'right',
    fontSize: 8,
  },
  needHelp: {
    fontFamily: 'RobotoItalic',
    fontSize: 8,
    marginHorizontal: 'auto',
    marginTop: 50,
  },
  needHelp2: {
    fontFamily: 'RobotoItalic',
    fontSize: 8,
    marginHorizontal: 'auto',
    marginTop: 5,
  },
  globalTech: {
    fontFamily: 'RobotoBoldItalic',
    fontSize: 8,
    marginHorizontal: 'auto',
    marginTop: 5,
  },
  amount: {
    marginRight: 20,
  },
  footerTop: {
    marginTop: 200,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    width: 350,
    marginLeft: 110,
  },
  footerTopLinks: {
    fontSize: 8,
    opacity: 0.5,
    marginHorizontal: 'auto',
  },
  footerCopyright: {
    fontSize: 8,
    opacity: 0.5,
    marginHorizontal: 'auto',
    marginTop: 10,
  },
});

const Invoice: FC<any> = () => {
  const { t } = useTranslation();

  return (
    <PDFViewer style={styles.viewer}>
      <Document>
        <Page size='A4' style={styles.page}>
          <View style={styles.container}>
            <Image src={resideoBlackLogo} style={styles.image} />
            <View style={styles.addressWrapper}>
              <Text style={styles.smallBoldText}>{t('resideo')}</Text>
              <Text style={styles.lightText}>{t('invoicePdf.address')}</Text>
              <Text style={styles.lightText}>{t('invoicePdf.city')}</Text>
              <Text style={styles.lightText}>{t('invoicePdf.us')}</Text>
              <Text style={styles.lightText}>{t('invoicePdf.fedId')}</Text>
            </View>
            <View style={{ flexDirection: 'column' }}>
              <Text style={styles.pageTitle}>{t('invoicePdf.invoice')}</Text>
              <Text style={{ fontSize: 10, fontFamily: 'RobotoBold' }}>
                {t('invoicePdf.invoiceNum')}
                <Text style={styles.lightText}>{t('invoicePdf.num')}</Text>
              </Text>
            </View>
            <View style={styles.billToWrapper}>
              <Text style={styles.smallBoldText}>{t('invoicePdf.billTo')}</Text>
              <Text style={styles.lightText}>{t('invoicePdf.fullName')}</Text>
              <Text style={styles.lightText}>
                {t('invoicePdf.companyName')}
              </Text>
              <Text style={styles.lightText}>{t('invoicePdf.address')}</Text>
              <Text style={styles.lightText}>{t('invoicePdf.city')}</Text>
              <Text style={styles.lightText}>{t('invoicePdf.us')}</Text>
            </View>
            <View style={styles.table}>
              <Text style={styles.automaticallyCharged}>
                {t('invoicePdf.autoCharge')}
              </Text>
            </View>
            <View style={styles.tHead}>
              <Text style={styles.tData}>{t('invoicePdf.date')}</Text>
              <Text style={styles.tData}>{t('invoicePdf.description')}</Text>
              <Text style={styles.tData}>{t('invoicePdf.billingPeriod')}</Text>
              <Text style={styles.tData}>{t('invoicePdf.monitors')}</Text>
              <Text style={styles.amount}>{t('invoicePdf.amount')}</Text>
            </View>
            <View style={styles.tBody}>
              <Text style={styles.tData}>{t('invoicePdf.invoiceDate')}</Text>
              <Text style={styles.tData}>{t('invoicePdf.service')}</Text>
              <Text style={styles.tData}>
                {t('invoicePdf.billingPeriodDates')}
              </Text>
              <Text style={styles.tData}>5</Text>
              <Text style={[styles.amount, { fontFamily: 'RobotoBold' }]}>
                {t('invoicePdf.subtotalAmount')}
              </Text>
            </View>
            <View style={[styles.totalRow, { marginTop: 16 }]}>
              <View style={[styles.totalRowLeft]}>
                <Text>{t('invoicePdf.subtotalInUSD')}</Text>
              </View>
              <View style={[styles.totalRowRight, { marginRight: 174 }]}>
                <Text>{t('invoicePdf.subtotalAmount')}</Text>
              </View>
            </View>
            <View style={[styles.totalRow, { marginTop: 5 }]}>
              <View style={[styles.totalRowLeft]}>
                <Text>{t('invoicePdf.salesTax')}</Text>
              </View>
              <View style={[styles.totalRowRight, { marginRight: 170 }]}>
                <Text>{t('invoicePdf.salesTaxX')}</Text>
              </View>
            </View>
            <View style={[styles.totalRow, { marginTop: 20 }]}>
              <View style={[styles.totalRowLeft, { fontFamily: 'RobotoBold' }]}>
                <Text>{t('invoicePdf.totalInUSD')}</Text>
              </View>
              <View
                style={[
                  styles.totalRowRight,
                  { marginRight: 158, fontFamily: 'RobotoBold' },
                ]}>
                <Text>{t('invoicePdf.totalPlacer')}</Text>
              </View>
            </View>
            <View>
              <Text style={styles.needHelp}>
                *{' '}
                {t(
                  'mybusiness.servicesAndSubscriptions.contactProductSupportText'
                )}{' '}
              </Text>
              <Text style={styles.needHelp2}>
                <Text style={styles.globalTech}>
                  {t('mybusiness.servicesAndSubscriptions.globalTech')}
                </Text>
                {t(
                  'mybusiness.servicesAndSubscriptions.contactProductSupportText2'
                )}
              </Text>
            </View>
            <View style={styles.footerTop}>
              <Text style={styles.footerTopLinks}>RESIDEO.COM |</Text>
              <Text style={styles.footerTopLinks}>HONEYWELLHOME.COM |</Text>
              <Text style={styles.footerTopLinks}>PRIVACY POLICY |</Text>
              <Text style={styles.footerTopLinks}>TERMS & CONDITIONS</Text>
            </View>
            <View>
              <Text style={styles.footerCopyright}>
                {t('footer.copyright')}
              </Text>
            </View>
          </View>
        </Page>
      </Document>
    </PDFViewer>
  );
};

export default Invoice;
